import { Button, IconButton, Stack } from '@mui/material';
import { AssessmentsGradeUpdate, AssessmentsGradeUpdateOption } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { DraggableItem } from '@schooly/components/drag-and-drop';
import { usePrevious } from '@schooly/hooks/use-previous';
import { CrossIcon, PlusIcon } from '@schooly/style';
import { FC, useCallback, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { ControlTextField } from '../../../components/uikit-components/FormTextField/ControlTextField';
import { ControlModalHeaderInput } from '../../../components/uikit-components/Modal/ControlModalHeaderInput';
import {
  ModalContent,
  ModalMain,
  ModalSmall,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalFooterWithActions } from '../../../components/uikit-components/Modal/ModalFooterWithActions';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { SchoolAssessmentsGradeOptionRow } from './SchoolAssessmentsGradeOptionRow';
import { generateAssessmentsGradeListOption } from './utils';

export const GRADE_OPTIONS_DND_TYPE = 'GRADE_OPTIONS_DND_TYPE';

type InitialGrade = Omit<AssessmentsGradeUpdate, 'options'> & {
  options: (AssessmentsGradeUpdateOption & { has_assessments?: boolean })[];
};

export type AssessmentsGradeOptionForm = Omit<AssessmentsGradeUpdateOption, 'value'> & {
  has_assessments?: boolean;
  value: number | null;
};

export type AssessmentsGradeForm = Omit<AssessmentsGradeUpdate, 'options'> & {
  options: AssessmentsGradeOptionForm[];
};

type SchoolAssessmentsGradeModalContentProps = {
  grade?: InitialGrade;
  onClose: () => void;
  isSaving?: boolean;
  isNewItem: boolean;
  isArchiving?: boolean;
  onArchiveClick?: () => void;
  onSubmit: (v: AssessmentsGradeForm) => void;
  onDelete?: () => void;
};

export const SchoolAssessmentsGradeModalContent: FC<SchoolAssessmentsGradeModalContentProps> = ({
  grade: initialGrade,
  onClose,
  onArchiveClick,
  onSubmit,
  onDelete,
  isNewItem,
  isSaving,
  isArchiving,
}) => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  const form = useForm<AssessmentsGradeForm>({
    defaultValues: initialGrade
      ? {
          name: initialGrade.name,
          description: initialGrade.description,
          options: initialGrade.options.map(
            ({ name, value, has_assessments, id, description }) => ({
              name,
              description,
              value: value ?? null,
              has_assessments,
              ...(id ? { id } : {}),
            }),
          ),
        }
      : { name: '', description: '', options: [generateAssessmentsGradeListOption()] },
  });

  const {
    fields: optionFields,
    append,
    remove,
    replace,
    move,
  } = useFieldArray({ control: form.control, name: 'options' });

  const addOption = useCallback(() => {
    append(generateAssessmentsGradeListOption());
  }, [append]);

  const deleteOption = useCallback(
    (index: number) => () => {
      if (optionFields.length > 1) {
        remove(index);
      } else {
        replace([generateAssessmentsGradeListOption()]);
      }
    },
    [optionFields.length, remove, replace],
  );

  const canAddOrDeleteOption = !initialGrade?.options.some((o) => o.has_assessments);

  const validateOptionValue = useCallback(
    (value: number | null) => {
      if (value === null) return true;
      const options = form.getValues('options');

      const duplicateError = options.filter((o) => o.value === value).length > 1;
      if (duplicateError) {
        return formatMessage({ id: 'assessments-error-GradeOptionSameValue' });
      }

      //cannot rely on passing index due to DnD logic
      const index = options.findIndex((o) => o.value === value);
      if (index === -1) return true;

      const orderError = options.slice(0, index).some((o) => o.value !== null && o.value < value);

      if (orderError) {
        return formatMessage({ id: 'assessments-error-GradeOptionOrder' });
      }

      return true;
    },
    [form, formatMessage],
  );

  const triggerOptionValueValidation = useCallback(() => {
    form.getValues('options').forEach((o, i) => {
      if (o.value === null) return;
      const currentError = form.formState.errors.options?.[i]?.value;
      const error = validateOptionValue(o.value);

      if (typeof error === 'string') {
        form.setError(`options.${i}.value`, { message: error });
      } else if (currentError) {
        form.clearErrors(`options.${i}.value`);
      }
    });
  }, [form, validateOptionValue]);

  const optionsLength = optionFields.length;
  const prevOptionsLength = usePrevious(optionsLength);

  // trigger validation after option is removed
  useEffect(() => {
    if (prevOptionsLength && optionsLength < prevOptionsLength) {
      triggerOptionValueValidation();
    }
  }, [optionsLength, prevOptionsLength, triggerOptionValueValidation]);

  const handleClose = useCallback(async () => {
    if (form.formState.isDirty) {
      const isConfirmed = await getConfirmation({
        textId: 'school-edit-CloseUnsavedConfirmation',
      });
      if (!isConfirmed) return;
    }
    onClose();
  }, [form.formState.isDirty, getConfirmation, onClose]);

  return (
    <ModalSmall open onClose={handleClose}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <ModalHeader
            active
            disabled={isSaving}
            title={
              <ControlModalHeaderInput
                control={form.control}
                name="name"
                rules={{ required: true }}
                placeholder={formatMessage({ id: 'assessments-GradeSystemName' })}
                autoFocus={isNewItem}
              />
            }
          >
            {!isSaving && (
              <IconButton onClick={handleClose}>
                <CrossIcon />
              </IconButton>
            )}
          </ModalHeader>
          <ModalMain>
            <ModalContent active>
              <Stack alignItems="flex-start" gap={2} sx={{ height: '100%' }}>
                {optionFields?.map(({ id }, index) => (
                  <DraggableItem
                    accept={GRADE_OPTIONS_DND_TYPE}
                    key={id}
                    index={index}
                    onMove={move}
                    onDrop={triggerOptionValueValidation}
                    renderContent={() => (
                      <SchoolAssessmentsGradeOptionRow
                        id={id}
                        index={index}
                        validateOptionValue={validateOptionValue}
                        deleteOption={canAddOrDeleteOption ? deleteOption : undefined}
                      />
                    )}
                  />
                ))}
                {canAddOrDeleteOption && (
                  <Button variant="outlined" startIcon={<PlusIcon />} onClick={addOption}>
                    <FormattedMessage id="assessments-AddGrade" />
                  </Button>
                )}
                <ControlTextField
                  name="description"
                  control={form.control}
                  label={formatMessage({ id: 'assessments-AddGradeDescription' })}
                  fullWidth
                  multiline
                  rows={4}
                  canClear
                />
              </Stack>
            </ModalContent>
          </ModalMain>
          <ModalFooterWithActions
            active
            saving={isSaving}
            archiving={isArchiving}
            showDeleteButton={!!onDelete}
            showArchiveButton={!!onArchiveClick}
            onArchiveClick={onArchiveClick}
            onDeleteClick={onDelete}
            isNewItem={isNewItem}
          />
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
